import type { VFC } from 'react';
import { memo, useCallback, useRef } from 'react';
import isEqual from 'lodash/isEqual';

import { browserMetrics } from '@atlassian/browser-metrics';
import type { PageSegmentLoadMetric } from '@atlassian/browser-metrics/types';

import { VIEW_BLOG, VIEW_PAGE } from '@confluence/named-routes/entry-points/routes';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';

import type { ActionTiming } from './usePerformanceAction';
import { usePerformanceAction } from './usePerformanceAction';
import { PAGE_SEGMENT_MARK_PREFIX_END } from './metric-keys';
import { browserMetricsState } from './utils';
import { getViewTransitionSource } from './viewTransitionSource';

export type Props = {
	/**
	 * The metric to stop.
	 */
	metric: PageSegmentLoadMetric;
	/**
	 * A callback that is fired right after the metric is started.
	 */
	onComplete?: () => void;
	/**
	 * A override to use for the metric stop time.
	 */
	stopTime?: number;
	/**
	 * The timing to use when starting the metric.
	 *
	 */
	timing?: ActionTiming;
	/**
	 * Custom data to add to the event's payload
	 */
	customData?: Record<string, any>;
	/**
	 * OnComplete might have sideeffects like measuring performance.
	 * This function gives an opportunity to enrich customData with more fields after the "onComplete" call
	 */
	augmentCustomDataAfterComplete?: (customData: Record<string, any>) => any;
};

const noop = () => {};

const PageSegmentLoadEndComponent: VFC<Props> = memo(
	({
		customData = {},
		metric,
		onComplete = noop,
		stopTime,
		timing,
		augmentCustomDataAfterComplete,
	}) => {
		// Use refs to avoid firing handlePageSegmentLoadMetricStop more than once despite changes to stopTime or customData
		const stopTimeRef = useRef(stopTime);
		const customDataRef = useRef(customData);
		const augmentCustomDataAfterCompleteRef = useRef(augmentCustomDataAfterComplete);

		stopTimeRef.current = stopTime;
		customDataRef.current = customData;
		augmentCustomDataAfterCompleteRef.current = augmentCustomDataAfterComplete;

		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			SSRMeasures.markMeasureEnd(`ssr-app/render/${metric.key}`);
		}

		const handlePageSegmentLoadMetricStop = useCallback(() => {
			if (metric) {
				onComplete();
				let finalCustomData;
				if (augmentCustomDataAfterCompleteRef.current) {
					finalCustomData = augmentCustomDataAfterCompleteRef.current(customDataRef.current);
				} else {
					finalCustomData = customDataRef.current;
				}

				const { route } = metric.getData();

				const shouldAddViewTransitionSource = route === VIEW_PAGE.name || route === VIEW_BLOG.name;
				if (shouldAddViewTransitionSource) {
					finalCustomData.viewTransitionSource = getViewTransitionSource();
				}

				browserMetrics.getPageLoadMetric().mark(`${PAGE_SEGMENT_MARK_PREFIX_END}:${metric.key}`);
				metric.stop({
					customData: {
						...finalCustomData,
						previousRoute: browserMetricsState.previousRoute,
						previousContentId: browserMetricsState.previousContentId,
						pageLoadInfo: getUniquePageLoadId(),
					},
					stopTime: stopTimeRef.current,
				});
			}
		}, [metric, onComplete]);

		usePerformanceAction(handlePageSegmentLoadMetricStop, timing);
		return null;
	},
	/**
	 * Use lodash isEqual as comparator to avoid unnecessary re-renders
	 * i.e. when an object is defined directly within customData prop
	 * `<PageLoadSegmentEnd ... customData={{ data: 'example' }} />`
	 * (Export only for testing)
	 */
	isEqual,
);

export const PageSegmentLoadEnd = withTransparentErrorBoundary({
	attribution: Attribution.BACKBONE,
})(PageSegmentLoadEndComponent);
